import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Paper from '@material-ui/core/Paper';
import { FormatDate, isEmpty } from '../../modules/appLibrary';

const POPUP_WIDTH = 1000;
const POPUP_HEIGHT = 620;
const POPUP_MARGIN = 12;

function clamp(value, min, max) {
    if (Number.isNaN(value)) return min;
    return Math.max(min, Math.min(max, value));
}

function getViewportSize() {
    if (typeof window === 'undefined') {
        return { width: POPUP_WIDTH, height: POPUP_HEIGHT };
    }

    return {
        width: window.innerWidth || POPUP_WIDTH,
        height: window.innerHeight || POPUP_HEIGHT
    };
}

function getPopupSize() {
    const viewport = getViewportSize();
    return {
        width: Math.min(POPUP_WIDTH, Math.max(POPUP_MARGIN * 2, viewport.width - POPUP_MARGIN * 2)),
        height: Math.min(POPUP_HEIGHT, Math.max(POPUP_MARGIN * 2, viewport.height - POPUP_MARGIN * 2))
    };
}

function getInitialPosition() {
    const viewport = getViewportSize();
    const popup = getPopupSize();
    const maxX = Math.max(POPUP_MARGIN, viewport.width - popup.width - POPUP_MARGIN);
    const maxY = Math.max(POPUP_MARGIN, viewport.height - popup.height - POPUP_MARGIN);

    return {
        x: clamp(Math.round((viewport.width - popup.width) / 2), POPUP_MARGIN, maxX),
        y: clamp(Math.round((viewport.height - popup.height) / 2), POPUP_MARGIN, maxY)
    };
}

function resolveValue(item, keys) {
    if (isEmpty(item) || isEmpty(keys)) return '';

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(item, key) && !isEmpty(item[key])) {
            return item[key];
        }
    }

    return '';
}

function normalizeImageSourceValue(value) {
    if (isEmpty(value)) return '';

    const text = String(value).trim();
    if (
        text.startsWith('data:') ||
        text.startsWith('http://') ||
        text.startsWith('https://') ||
        text.startsWith('/')
    ) {
        return text;
    }

    return '';
}

function resolveImageSource(item) {
    if (isEmpty(item)) return '';

    const directKeys = [
        'imageUrl',
        'imageSrc',
        'image',
        'pictureUrl',
        'pictureSrc',
        'PDT_PictureUrl',
        'PDT_PICTUREURL',
        'PDT_PictureFileUrl',
        'PDT_PICTUREFILEURL',
        'PDT_PictureBase64',
        'PDT_PICTUREBASE64'
    ];

    for (let i = 0; i < directKeys.length; i++) {
        const key = directKeys[i];
        if (Object.prototype.hasOwnProperty.call(item, key) && !isEmpty(item[key])) {
            const normalized = normalizeImageSourceValue(item[key]);
            if (!isEmpty(normalized)) return normalized;
        }
    }

    return normalizeImageSourceValue(resolveValue(item, ['PDT_PICTUREFILENM1', 'PDT_PictureFileNm1']));
}

function formatDateValue(value) {
    if (isEmpty(value)) return '';

    if (typeof value === 'string') {
        return FormatDate(value);
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}/${m}/${d}`;
    }

    return String(value);
}

function getCardCode(item) {
    return resolveValue(item, ['PDT_SYOHINCD', 'PDT_SyohinCD', 'PDT_HINBAN', 'hinban', 'productCode']);
}

function getCardName(item) {
    return resolveValue(item, ['PDT_HINMEINM', 'PDT_HinmeiNm', 'name', 'productName']);
}

function formatIntegerValue(value) {
    if (isEmpty(value)) return '';

    const normalized = String(value)
        .trim()
        .replace(/,/g, '')
        .replace(/[^\d.-]/g, '');
    if (isEmpty(normalized)) return String(value);

    const numberValue = Number(normalized);
    if (!Number.isFinite(numberValue)) {
        return String(value);
    }

    return new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(numberValue);
}

function PopupCardField(props) {
    const labelFontSize = props.labelFontSize || 9;
    const valueFontSize = props.valueFontSize || 14;
    const labelColor = props.labelColor || '#8c8c8c';
    const valueColor = props.valueColor || '#333';
    const valueWeight = props.valueWeight || 700;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', gap: 2, alignItems: 'flex-start' }}>
            <div
                style={{
                    color: labelColor,
                    fontSize: labelFontSize,
                    lineHeight: 1.08,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}
            >
                {props.label}
            </div>
            <div
                style={{
                    color: valueColor,
                    fontSize: valueFontSize,
                    lineHeight: 1.12,
                    fontWeight: valueWeight,
                    textAlign: 'left',
                    width: '100%',
                    minHeight: `${valueFontSize * 1.12}px`,
                    paddingLeft: '1em',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}
            >
                {props.value}
            </div>
        </div>
    );
}

function PopupMapCard(props) {
    const item = props.item || {};
    const imageSrc = resolveImageSource(item);
    const code = getCardCode(item);
    const name = getCardName(item);
    const itemCode = resolveValue(item, ['PDT_ITMCITEMCD', 'PDT_IKItemCd', 'PDT_IKITEMCD']);
    const category = resolveValue(item, ['PDTMD_CATEGORY', 'PDT_CATEGORY', 'category', 'categoryName']);
    const tenkaiDate = formatDateValue(resolveValue(item, ['SSDX_SHOPYMD', 'SSDX_SHOPYMDW', 'shopDate', 'tenkaiDate']));
    const draggable = props.draggable !== false;

    function handleOpenProductSub() {
        if (typeof props.onOpenProductSub === 'function') {
            props.onOpenProductSub(item);
        }
    }

    function handleDragStart(event) {
        if (typeof props.onDragStart === 'function') {
            props.onDragStart(item, props.source || 'popup', event);
        }
    }

    function handleDragEnd(event) {
        if (typeof props.onDragEnd === 'function') {
            props.onDragEnd(item, props.source || 'popup', event);
        }
    }

    function handleCardDoubleClick(e) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        handleOpenProductSub();
    }

    return (
        <Paper
            className="map-card-popup-card"
            elevation={0}
            draggable={draggable}
            onDragStart={draggable ? handleDragStart : undefined}
            onDragEnd={draggable ? handleDragEnd : undefined}
            onDoubleClick={handleCardDoubleClick}
            style={{
                display: 'flex',
                width: '100%',
                minWidth: 0,
                border: '1px solid #c8c8c8',
                boxShadow: 'none',
                backgroundColor: '#fff',
                overflow: 'hidden',
                cursor: draggable ? 'grab' : 'pointer',
                userSelect: 'none'
            }}
        >
            <div
                style={{
                    width: 220,
                    minWidth: 220,
                    backgroundColor: '#f8f8f8',
                    borderRight: '1px solid #dcdcdc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    boxSizing: 'border-box'
                }}
            >
                {isEmpty(imageSrc) ? (
                    <div style={{ color: '#888', fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>no image</div>
                ) : (
                    <img
                        src={imageSrc}
                        alt={name || code || 'card'}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                    />
                )}
            </div>
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '12px 14px 12px 12px',
                    boxSizing: 'border-box'
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <PopupCardField
                        label="品番"
                        value={code}
                        valueFontSize={14}
                        valueWeight={700}
                    />
                    <PopupCardField
                        label="品名"
                        value={name || '品名未設定'}
                    />
                    <PopupCardField label="アイテム" value={itemCode} labelFontSize={9} />
                    <PopupCardField label="カテゴリ" value={category} labelFontSize={9} />
                    <PopupCardField label="店頭展開日" value={tenkaiDate} labelFontSize={9} />
                </div>
            </div>
        </Paper>
    );
}

export default function TenkaiMdMapCardPopupFloating(props) {
    const open = Boolean(props.open);
    const coveredByModal = props.coveredByModal === true;
    const cards = useMemo(() => (Array.isArray(props.items) ? props.items : []), [props.items]);
    const title = props.title || 'MAPカード未分類';
    const handleClose = props.onClose || (() => { });
    const popupRef = useRef(null);
    const [position, setPosition] = useState(() => getInitialPosition());

    useEffect(() => {
        if (!open) return;
        setPosition(getInitialPosition());
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        function handleResize() {
            const viewport = getViewportSize();
            const popup = popupRef.current ? popupRef.current.getBoundingClientRect() : getPopupSize();
            const width = popup.width || (popup.right - popup.left) || POPUP_WIDTH;
            const height = popup.height || (popup.bottom - popup.top) || POPUP_HEIGHT;
            const maxX = Math.max(POPUP_MARGIN, viewport.width - width - POPUP_MARGIN);
            const maxY = Math.max(POPUP_MARGIN, viewport.height - height - POPUP_MARGIN);

            setPosition((prev) => ({
                x: clamp(prev.x, POPUP_MARGIN, maxX),
                y: clamp(prev.y, POPUP_MARGIN, maxY)
            }));
        }

        function handleKeyDown(e) {
            if (e.key === 'Escape') {
                handleClose();
            }
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, handleClose]);

    if (!open || typeof document === 'undefined') return null;

    const popup = (
        <Draggable handle=".map-card-popup-titlebar" cancel=".map-card-popup-close, .map-card-popup-body, .map-card-popup-card">
            <Paper
                ref={popupRef}
                elevation={0}
                role="dialog"
                aria-modal="false"
                aria-label={title}
                style={{
                    position: 'fixed',
                    left: position.x,
                    top: position.y,
                    width: POPUP_WIDTH,
                    maxWidth: 'calc(100vw - 24px)',
                    height: POPUP_HEIGHT,
                    maxHeight: 'calc(100vh - 24px)',
                    borderRadius: 0,
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: '2px solid #222',
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    zIndex: coveredByModal ? 1200 : 1600
                }}
            >
                <div
                    className="map-card-popup-titlebar"
                    style={{
                        position: 'relative',
                        borderBottom: '2px solid #222',
                        textAlign: 'center',
                        padding: '6px 40px 5px',
                        minHeight: 36,
                        fontSize: 20,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        cursor: 'move',
                        userSelect: 'none',
                        backgroundColor: '#efefef',
                        boxSizing: 'border-box'
                    }}
                >
                    <span style={{ pointerEvents: 'none' }}>{title}</span>
                    <IconButton
                        className="map-card-popup-close"
                        size="small"
                        onClick={handleClose}
                        style={{ position: 'absolute', right: 4, top: 3 }}
                    >
                        <Icon style={{ fontSize: 16 }}>close</Icon>
                    </IconButton>
                </div>

                <div
                    className="map-card-popup-body"
                    style={{
                        padding: 8,
                        flex: 1,
                        minHeight: 0,
                        backgroundColor: '#fff',
                        boxSizing: 'border-box'
                    }}
                >
                    <div
                        style={{
                            height: '100%',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingRight: 2
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 10,
                                alignContent: 'start',
                                justifyContent: 'start',
                                alignItems: 'stretch'
                            }}
                        >
                            {cards.length === 0 ? (
                                <div
                                    style={{
                                        gridColumn: '1 / -1',
                                        padding: '12px 4px',
                                        color: '#777',
                                        fontSize: 13
                                    }}
                                >
                                    表示対象がありません
                                </div>
                            ) : (
                                cards.map((card, index) => (
                                    <PopupMapCard
                                        key={card.id || card.code || card.PDT_SYOHINCD || index}
                                        item={card}
                                        source="popup"
                                        draggable
                                        onOpenMaster={props.onOpenMaster}
                                        onOpenProductSub={props.onOpenProductSub}
                                        onDragStart={props.onDragStart}
                                        onDragEnd={props.onDragEnd}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </Paper>
        </Draggable>
    );

    return ReactDOM.createPortal(popup, document.body);
}

